import React, { useState, useMemo, useCallback } from 'react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { formatCivilDate, calculateAge } from '../utils/date';
import { isStudentUnnamed, studentDisplayName } from '../utils/student';
import { formatDayMonth } from '../utils/terms';
import { useAuth } from '../hooks/useAuth';
import { useLocationContext } from '../hooks/useLocationContext';
import { ScopedLink } from '../components/ScopedLink';
import {
  useLocalGetStudentsByParentQuery,
  useGetEnrichedStudentsQuery,
  useBulkDeactivateStudentsMutation,
  useExportStudentsMutation,
  useResendStudentOnboardingMutation,
} from '../store/api/localApi';
import { useToast } from '../components/Toast';
import ConfirmDialog from '../components/ConfirmDialog';
import RoleGate from '../components/RoleGate';
import DataTable, { Column } from '../components/DataTable';
import BulkActionBar from '../components/BulkActionBar';
import AddStudentModal from './students/AddStudentModal';
import BulkClassModal from './students/BulkClassModal';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  AcademicCapIcon,
  ArrowDownTrayIcon,
  UserPlusIcon,
  UserMinusIcon,
} from '@heroicons/react/24/outline';

/** The students-list status filter: who the list shows. */
type StudentsStatusView = 'current' | 'left' | 'archived';

// Enriched student type returned by the enriched endpoint
interface EnrichedStudent {
  id: string;
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  locationId: string;
  organizationId?: string;
  parentId: string;
  grade?: string;
  status: 'PENDING_START' | 'ACTIVE' | 'LEFT';
  // Dated lifecycle + server-derived flags (spec §2/§3) used for badging.
  startDate?: string;
  expectedEndDate?: string;
  leftAt?: string;
  isLeavingScheduled?: boolean;
  isArchived?: boolean;
  registrationStatus?: 'PENDING' | 'COMPLETE';
  relationship?: string;
  // `startsOn`: the child starts in this class later; the days are the ones they will have.
  classes: Array<{ id: string; name: string; daysOfWeek: number[]; startsOn?: string }>;
  parents?: Array<{
    id: string;
    name: string;
    email?: string;
    relationship?: string;
    isPrimary: boolean;
  }>;
  parentName?: string;
  parentEmail?: string;
  createdAt: string;
  updatedAt: string;
}

const DAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

// Parent card view - simple, no bulk ops
const ParentStudentCard: React.FC<{ student: any }> = ({ student }) => {
  return (
    <ScopedLink
      to={`/students/${student.id}`}
      data-testid={`student-${student.id}`}
      className="block hover:bg-gray-50 px-4 py-4 sm:px-6"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <div className="h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
            <span className="text-lg font-medium text-primary-600">
              {student.firstName[0]}
              {student.lastName[0]}
            </span>
          </div>
          <div className="ml-4">
            <div className="text-lg font-medium text-gray-900">
              {student.firstName} {student.lastName}
            </div>
            <div className="text-sm text-gray-500 space-x-4">
              {student.grade && <span>{student.grade}</span>}
              <span>•</span>
              <span>Age {calculateAge(student.dateOfBirth)}</span>
            </div>
          </div>
        </div>
        <div className="text-sm text-gray-400">View details →</div>
      </div>
    </ScopedLink>
  );
};

// Parent/Guardian cell. Shows the primary parent inline; if more parents
// are linked, a "+N" pill opens a popover listing every linked parent
// (name, email, relationship label, primary tag). Keeps the table row
// height stable while ensuring every guardian is reachable without
// drilling into the student detail page.
const ParentGuardianCell: React.FC<{
  student: {
    id: string;
    parents?: EnrichedStudent['parents'];
    parentName?: string;
    parentEmail?: string;
  };
}> = ({ student }) => {
  const parents = student.parents ?? [];
  const primary =
    parents.find((p) => p.isPrimary) ??
    parents[0] ??
    // Older payloads (pre-parents-array): synthesise from legacy fields so
    // the column renders during the rolling deploy window.
    (student.parentName
      ? { id: 'legacy', name: student.parentName, email: student.parentEmail, isPrimary: true }
      : null);
  if (!primary) {
    return <span className="text-gray-400 text-sm">-</span>;
  }
  const others = parents.filter((p) => p.id !== primary.id);
  return (
    <div className="flex items-start gap-2">
      <div className="min-w-0">
        <div className="text-gray-900 text-sm truncate">{primary.name}</div>
        {primary.email && <div className="text-gray-400 text-xs truncate">{primary.email}</div>}
      </div>
      {others.length > 0 && (
        <Popover className="relative shrink-0">
          <PopoverButton
            data-testid={`student-parents-toggle-${student.id}`}
            aria-label={`Show ${others.length} additional parent${others.length === 1 ? '' : 's'}`}
            className="inline-flex items-center justify-center min-w-[1.75rem] h-6 px-1.5 rounded-full bg-primary-50 text-primary-700 text-xs font-medium border border-primary-100 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            +{others.length}
          </PopoverButton>
          <PopoverPanel
            anchor={{ to: 'bottom end', gap: 4 }}
            data-testid={`student-parents-panel-${student.id}`}
            className="z-20 w-72 rounded-md border border-gray-200 bg-white shadow-lg ring-1 ring-black/5 p-3 space-y-2"
          >
            {parents.map((p) => (
              <div
                key={p.id}
                data-testid={`student-parent-${student.id}-${p.id}`}
                className="text-sm"
              >
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900 truncate">{p.name}</span>
                  {p.isPrimary && (
                    <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                      Primary
                    </span>
                  )}
                </div>
                {p.relationship && <div className="text-xs text-gray-500">{p.relationship}</div>}
                {p.email && <div className="text-xs text-gray-500 truncate">{p.email}</div>}
              </div>
            ))}
          </PopoverPanel>
        </Popover>
      )}
    </div>
  );
};

// Days-of-week pill display
const DaysPills: React.FC<{ classId: string; days: number[] }> = ({ classId, days }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((d) => (
      <span
        key={d}
        data-testid={`class-day-${classId}-${d}`}
        data-selected={days.includes(d)}
        className={`w-5 h-5 rounded-full text-xs flex items-center justify-center ${
          days.includes(d)
            ? 'bg-primary-100 text-primary-700 font-medium'
            : 'bg-gray-100 text-gray-400'
        }`}
      >
        {DAY_LABELS[d]}
      </span>
    ))}
  </div>
);

const Students: React.FC = () => {
  const { showError, showSuccess } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [gradeFilter, setGradeFilter] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkEnrollModal, setShowBulkEnrollModal] = useState(false);
  // Which children the list shows. Departed children are hidden by default so
  // a child who has left is never mistaken for the live one (a duplicate
  // profile was once edited for weeks because both sat side by side in the
  // list). 'left' adds them, 'archived' adds the ones gone over a year too.
  const [statusView, setStatusView] = useState<StudentsStatusView>('current');
  const includeArchived = statusView === 'archived';
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState('lastName');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const { user, isParent, isAdmin, isOrgLevel } = useAuth();
  const { effectiveLocationId: activeLocationId, effectiveOrgId } = useLocationContext();

  // Parent: simple query
  const { data: parentStudents } = useLocalGetStudentsByParentQuery(user?.id || '', {
    skip: !isParent || !user?.id,
  });

  // Staff/Admin: enriched query with classes.
  // Always pass locationId when we have one (super admin scoped into a location
  // via URL is org-level but should still get only that location's students).
  const enrichedParams = activeLocationId
    ? { locationId: activeLocationId, includeArchived }
    : { includeArchived };
  const { data: enrichedData } = useGetEnrichedStudentsQuery(enrichedParams, {
    skip: isParent || (!isOrgLevel && !activeLocationId),
  });

  const enrichedStudents: EnrichedStudent[] = enrichedData?.students || [];

  // Available classes for the filter dropdown — derived from the enriched
  // students response, not a separate /classes round trip. Saves one HTTP
  // call per page load. Trade-off: classes with zero students don't appear
  // in the filter, but filtering by "a class with no students" is never
  // useful anyway.
  const availableClasses = useMemo(() => {
    const seen = new Map<string, { id: string; name: string }>();
    for (const s of enrichedStudents) {
      for (const c of s.classes) {
        if (!seen.has(c.id)) seen.set(c.id, { id: c.id, name: c.name });
      }
    }
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [enrichedStudents]);

  // Filtering and sorting for enriched students
  const filteredStudents = useMemo(() => {
    let result = [...enrichedStudents];

    if (statusView === 'current') {
      result = result.filter((s) => s.status !== 'LEFT');
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (s) =>
          `${s.firstName} ${s.lastName}`.toLowerCase().includes(term) ||
          s.parentName?.toLowerCase().includes(term) ||
          s.parentEmail?.toLowerCase().includes(term)
      );
    }
    if (gradeFilter) {
      result = result.filter((s) => s.grade === gradeFilter);
    }
    if (classFilter) {
      result = result.filter((s) => s.classes.some((c) => c.id === classFilter));
    }

    // Sort. Names/DOB may be empty (rows pre-registration) — fall back to ''
    // / Infinity so unfilled rows sort to the end. Children who have left
    // always sort after current ones, whatever the column, so the live rows
    // stay together at the top.
    result.sort((a, b) => {
      const aLeft = a.status === 'LEFT' ? 1 : 0;
      const bLeft = b.status === 'LEFT' ? 1 : 0;
      if (aLeft !== bLeft) return aLeft - bLeft;
      let aVal = '',
        bVal = '';
      switch (sortField) {
        case 'lastName':
          aVal = a.lastName || '';
          bVal = b.lastName || '';
          break;
        case 'firstName':
          aVal = a.firstName || '';
          bVal = b.firstName || '';
          break;
        case 'grade':
          aVal = a.grade || '';
          bVal = b.grade || '';
          break;
        case 'age': {
          const ageA = calculateAge(a.dateOfBirth) ?? Number.POSITIVE_INFINITY;
          const ageB = calculateAge(b.dateOfBirth) ?? Number.POSITIVE_INFINITY;
          return sortDir === 'asc' ? ageA - ageB : ageB - ageA;
        }
        case 'parentName':
          aVal = a.parentName || '';
          bVal = b.parentName || '';
          break;
        default:
          aVal = a.lastName || '';
          bVal = b.lastName || '';
      }
      const cmp = aVal.localeCompare(bVal);
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [enrichedStudents, statusView, searchTerm, gradeFilter, classFilter, sortField, sortDir]);

  // Parent filtering
  const filteredParentStudents = useMemo(() => {
    if (!parentStudents) return [];
    if (!searchTerm) return parentStudents;
    const term = searchTerm.toLowerCase();
    return parentStudents.filter((s) =>
      `${s.firstName} ${s.lastName}`.toLowerCase().includes(term)
    );
  }, [parentStudents, searchTerm]);

  const grades = useMemo(
    () => Array.from(new Set(enrichedStudents.map((s) => s.grade).filter(Boolean))).sort(),
    [enrichedStudents]
  );

  const handleSort = useCallback(
    (field: string) => {
      if (sortField === field) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortField(field);
        setSortDir('asc');
      }
    },
    [sortField]
  );

  const [bulkDeactivate] = useBulkDeactivateStudentsMutation();
  const [exportStudents] = useExportStudentsMutation();
  const [resendOnboarding, { isLoading: isResending }] = useResendStudentOnboardingMutation();
  // Track which row's resend is in flight so we only disable that one button.
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);

  const handleBulkDeactivate = () => {
    setShowDeactivateConfirm(true);
  };

  const confirmBulkDeactivate = async () => {
    setShowDeactivateConfirm(false);
    try {
      await bulkDeactivate({ studentIds: Array.from(selectedIds) }).unwrap();
      setSelectedIds(new Set());
    } catch (err: any) {
      showError(err.data?.error || 'Failed to deactivate students');
    }
  };

  // Triggered from the inline "Resend" affordance on pending rows. Sends a
  // fresh signup-invite email + (if applicable) re-issues the registration
  // form invitation. Reuses the same backend endpoint the Student Detail
  // page uses, so the side effects are identical.
  const handleResendInvite = async (e: React.MouseEvent, studentId: string) => {
    // The row is wrapped in a Link to the detail page — stop the click here so
    // we don't navigate away while the resend is in flight.
    e.preventDefault();
    e.stopPropagation();
    if (resendingId) return;
    setResendingId(studentId);
    try {
      const result = await resendOnboarding(studentId).unwrap();
      const formNote = result.formInvitationCreated
        ? ' Registration form issued.'
        : result.formInvitationResent
        ? ' Registration form re-sent.'
        : '';
      showSuccess(`Invite resent.${formNote}`);
    } catch (err: any) {
      const msg = err?.data?.message || err?.data?.error || 'Failed to resend invite';
      showError(msg);
    } finally {
      setResendingId(null);
    }
  };

  const handleExport = async () => {
    try {
      const blob = await exportStudents({
        locationId: activeLocationId || undefined,
        organizationId: !activeLocationId ? effectiveOrgId || undefined : undefined,
        includeInactive: includeArchived,
        grade: gradeFilter || undefined,
        classId: classFilter || undefined,
        search: searchTerm || undefined,
      }).unwrap();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `students-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      showError('Failed to export students');
    }
  };

  const columns: Column<EnrichedStudent>[] = [
    {
      key: 'lastName',
      header: 'Name',
      sortable: true,
      render: (s) => {
        // Pending rows have no child name yet — the parent hasn't filled in
        // the registration form. Show the em-dash placeholder for the name
        // and an italic "Awaiting registration" subtitle so the row reads
        // as in-progress at a glance. The parent email lives in its own
        // column, so we don't duplicate it here.
        const unnamed = isStudentUnnamed(s);
        return (
          <ScopedLink
            to={`/students/${s.id}`}
            className="flex items-center gap-3 group"
            data-testid={`student-${s.id}`}
          >
            <div
              className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                unnamed ? 'bg-gray-100' : 'bg-primary-100'
              }`}
            >
              <span
                className={`text-xs font-medium ${unnamed ? 'text-gray-400' : 'text-primary-600'}`}
              >
                {unnamed ? '?' : `${s.firstName?.[0] ?? ''}${s.lastName?.[0] ?? ''}`}
              </span>
            </div>
            <div className="min-w-0">
              <div
                className={`font-medium group-hover:text-primary-600 truncate ${
                  unnamed ? 'text-gray-400' : 'text-gray-900'
                }`}
              >
                {studentDisplayName(s)}
              </div>
              {unnamed && <div className="text-xs text-gray-400 italic">Awaiting registration</div>}
            </div>
          </ScopedLink>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      className: 'w-36',
      render: (s) => {
        // Lifecycle badges (spec §2). A LEFT child wins over everything — it is
        // the terminal state — so its badge (and the archived variant) renders
        // first regardless of where the row was in registration.
        if (s.status === 'LEFT') {
          const label = s.leftAt ? `Left ${formatCivilDate(s.leftAt)}` : 'Left';
          return (
            <span
              data-testid="student-badge-left"
              className={`inline-flex items-center whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-medium ${
                s.isArchived ? 'bg-gray-100 text-gray-500' : 'bg-gray-100 text-gray-600'
              }`}
            >
              {label}
              {s.isArchived ? ' · Archived' : ''}
            </span>
          );
        }
        // PENDING_START: accepted and dated, not attending yet.
        if (s.status === 'PENDING_START') {
          return (
            <span
              data-testid="student-badge-pending"
              className="inline-flex items-center whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-medium bg-sky-100 text-sky-800"
            >
              {s.startDate ? `Starts ${formatCivilDate(s.startDate)}` : 'Pending start'}
            </span>
          );
        }
        // LEAVING_SCHEDULED: ACTIVE with an announced departure. Quiet badge —
        // the child is still attending, this is just a heads-up.
        if (s.isLeavingScheduled) {
          return (
            <span
              data-testid="student-badge-leaving"
              className="inline-flex items-center whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700"
            >
              {s.expectedEndDate ? `Leaving ${formatCivilDate(s.expectedEndDate)}` : 'Leaving soon'}
            </span>
          );
        }
        if ((s.registrationStatus || 'PENDING') === 'PENDING') {
          // Render the "Resend" affordance inline so admins can chase a
          // parent without leaving the list. Default-hidden, revealed on row
          // hover via the parent <tr>'s `.group` class. While the request is
          // in flight, we show a static "Resending…" label instead of the
          // button so double-clicks don't queue a second request.
          const rowResending = resendingId === s.id;
          return (
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                Pending
              </span>
              {rowResending ? (
                <span className="text-xs text-gray-500">Resending…</span>
              ) : (
                // Always visible — hover-reveal patterns are dead on touch
                // devices (no hover state), so the button was invisible on
                // mobile and tablet before. Showing it inline at all sizes is
                // the simplest fix and the cleanest workflow on every device.
                <button
                  type="button"
                  onClick={(e) => handleResendInvite(e, s.id)}
                  disabled={isResending}
                  className="text-xs font-medium text-primary-600 hover:text-primary-700 hover:underline disabled:opacity-50"
                  data-testid={`resend-invite-${s.id}`}
                >
                  Resend
                </button>
              )}
            </div>
          );
        }
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Active
          </span>
        );
      },
    },
    {
      key: 'age',
      header: 'Age',
      sortable: true,
      className: 'w-16',
      render: (s) => <span className="text-gray-600">{calculateAge(s.dateOfBirth)}</span>,
    },
    {
      key: 'grade',
      header: 'Grade',
      sortable: true,
      className: 'w-24',
      render: (s) => <span className="text-gray-600">{s.grade || '-'}</span>,
    },
    {
      key: 'classes',
      header: 'Classes',
      render: (s) => (
        <div className="flex flex-col gap-1.5">
          {s.classes.length === 0 ? (
            <span className="text-gray-400 text-xs">Not enrolled</span>
          ) : (
            s.classes.map((c) => (
              <div key={c.id} className="flex items-center gap-2">
                <span
                  data-testid={`class-badge-${c.id}`}
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 whitespace-nowrap"
                >
                  {c.name}
                </span>
                <DaysPills classId={c.id} days={c.daysOfWeek} />
                {c.startsOn && (
                  <span
                    data-testid={`class-badge-starts-${c.id}`}
                    className="text-xs text-gray-500 whitespace-nowrap"
                  >
                    from {formatDayMonth(c.startsOn)}
                  </span>
                )}
              </div>
            ))
          )}
        </div>
      ),
    },
    {
      key: 'parentName',
      header: 'Parent/Guardian',
      sortable: true,
      render: (s) => <ParentGuardianCell student={s} />,
    },
  ];

  // ========== PARENT VIEW ==========
  if (isParent) {
    return (
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900">My Children</h2>
            <p className="mt-1 text-sm text-gray-500">
              View and manage your children's information
            </p>
          </div>

          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            {filteredParentStudents.length === 0 ? (
              <div className="text-center py-12">
                <AcademicCapIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No children registered</h3>
              </div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {filteredParentStudents.map((s) => (
                  <li key={s.id}>
                    <ParentStudentCard student={s} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ========== STAFF/ADMIN VIEW ==========
  return (
    <div className="py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="md:flex md:items-center md:justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Students</h2>
            <p className="mt-1 text-sm text-gray-500">
              {filteredStudents.length} student{filteredStudents.length !== 1 ? 's' : ''}
              {gradeFilter && ` in ${gradeFilter}`}
              {classFilter &&
                ` enrolled in ${
                  (availableClasses as any[])?.find((c: any) => c.id === classFilter)?.name ||
                  'class'
                }`}
            </p>
          </div>
          <div className="mt-4 flex gap-2 md:mt-0">
            <button
              onClick={handleExport}
              data-testid="export-students-btn"
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <ArrowDownTrayIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
              Export CSV
            </button>
            <RoleGate when={isAdmin}>
              <button
                onClick={() => setShowAddModal(true)}
                data-testid="add-student-btn"
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
              >
                <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                Add Student
              </button>
            </RoleGate>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white shadow rounded-lg mb-4">
          <div className="px-4 py-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1 relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  data-testid="student-search"
                  className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Search by name, parent..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-2">
                <FunnelIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                {grades.length > 0 && (
                  <select
                    data-testid="grade-filter"
                    className="border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                    value={gradeFilter}
                    onChange={(e) => setGradeFilter(e.target.value)}
                  >
                    <option value="">All Grades</option>
                    {grades.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </select>
                )}
                {Array.isArray(availableClasses) && availableClasses.length > 0 && (
                  <select
                    data-testid="class-filter"
                    className="border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                    value={classFilter}
                    onChange={(e) => setClassFilter(e.target.value)}
                  >
                    <option value="">All Classes</option>
                    {(availableClasses as any[]).map((c: any) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                )}
                <select
                  data-testid="students-status-filter"
                  aria-label="Which children to show"
                  className="border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                  value={statusView}
                  onChange={(e) => {
                    setStatusView(e.target.value as StudentsStatusView);
                    // A selection made in one view must not act on rows hidden by the next.
                    setSelectedIds(new Set());
                  }}
                >
                  <option value="current">Current children</option>
                  <option value="left">Include children who have left</option>
                  <option value="archived">Include archived (left over a year ago)</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Bulk Actions */}
        <div className="mb-4">
          <BulkActionBar
            selectedCount={selectedIds.size}
            onClear={() => setSelectedIds(new Set())}
            actions={[
              {
                label: 'Enroll in Class',
                icon: <UserPlusIcon className="h-4 w-4" />,
                onClick: () => setShowBulkEnrollModal(true),
                variant: 'primary',
                testId: 'bulk-enroll-btn',
              },
              {
                label: 'Deactivate',
                icon: <UserMinusIcon className="h-4 w-4" />,
                onClick: handleBulkDeactivate,
                variant: 'danger',
                testId: 'bulk-deactivate-btn',
              },
              {
                label: 'Export Selected',
                icon: <ArrowDownTrayIcon className="h-4 w-4" />,
                onClick: handleExport,
                testId: 'bulk-export-btn',
              },
            ]}
          />
        </div>

        {/* Table */}
        <DataTable
          data={filteredStudents}
          columns={columns}
          keyField="id"
          selectable={isAdmin}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          pageSize={25}
          emptyMessage={
            searchTerm || gradeFilter || classFilter || statusView !== 'current'
              ? 'No students match your filters.'
              : enrichedStudents.length > 0
              ? 'Every child at this location has left. Choose "Include children who have left" to see them.'
              : 'No students at this location yet.'
          }
          sortField={sortField}
          sortDirection={sortDir}
          onSort={handleSort}
          testIdPrefix="students"
          // Opacity, not a text colour: cells set their own colours, which would
          // override an inherited one and leave the name as bold as a live child's.
          rowClassName={(s) => (s.status === 'LEFT' ? 'opacity-60' : '')}
        />

        {/* Summary stats */}
        {filteredStudents.length > 0 && (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-white shadow rounded-lg px-4 py-3">
              <div className="text-2xl font-semibold text-gray-900">{filteredStudents.length}</div>
              <div className="text-xs text-gray-500">Total Students</div>
            </div>
            <div className="bg-white shadow rounded-lg px-4 py-3">
              <div className="text-2xl font-semibold text-gray-900">
                {filteredStudents.filter((s) => s.classes.length > 0).length}
              </div>
              <div className="text-xs text-gray-500">Enrolled</div>
            </div>
            <div className="bg-white shadow rounded-lg px-4 py-3">
              <div className="text-2xl font-semibold text-gray-900">
                {filteredStudents.filter((s) => s.classes.length === 0).length}
              </div>
              <div className="text-xs text-gray-500">Not Enrolled</div>
            </div>
            <div className="bg-white shadow rounded-lg px-4 py-3">
              <div className="text-2xl font-semibold text-gray-900">{grades.length}</div>
              <div className="text-xs text-gray-500">Grades</div>
            </div>
          </div>
        )}

        {/* Modals */}
        {showAddModal && <AddStudentModal onClose={() => setShowAddModal(false)} />}
        {showBulkEnrollModal && (
          <BulkClassModal
            studentIds={Array.from(selectedIds)}
            onClose={() => setShowBulkEnrollModal(false)}
            onSuccess={() => {
              setShowBulkEnrollModal(false);
              setSelectedIds(new Set());
            }}
          />
        )}
        <ConfirmDialog
          isOpen={showDeactivateConfirm}
          onConfirm={confirmBulkDeactivate}
          onCancel={() => setShowDeactivateConfirm(false)}
          title="Deactivate Students"
          message={`Deactivate ${selectedIds.size} student(s)? This will withdraw them from all classes.`}
          confirmLabel="Deactivate"
          variant="danger"
        />
      </div>
    </div>
  );
};

export default Students;
